import React, { useState } from "react";
import {
    Container,
    TextField,
    Button,
    Typography,
    Checkbox,
    FormControlLabel,
    Paper,
    Box,
    IconButton,
    InputAdornment,
    Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const schema = yup.object().shape({
    email: yup.string().email("Email invalide").required("Email requis"),
    password: yup.string().required("Mot de passe requis"),
    remember: yup.boolean(),
});

const Login = () => {
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();

    // État pour afficher/masquer le mot de passe et stocker l'erreur de l'API
    const [showPassword, setShowPassword] = useState(false);
    const [apiError, setApiError] = useState("");

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            email: localStorage.getItem("remembered_email") || "",
            password: "",
            remember: !!localStorage.getItem("remembered_email"),
        },
    });

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const onSubmit = async (data) => {
        setApiError(""); // Réinitialise l'erreur à chaque tentative

        try {
            const userData = await login(data.email, data.password, data.remember);

            // Gestion du stockage "Se souvenir de moi" dans le navigateur
            if (data.remember) {
                localStorage.setItem("remembered_email", data.email);
            } else {
                localStorage.removeItem("remembered_email");
            }

            // Redirection selon le rôle
            const role = Number(userData.role);
            if (role === 3) {
                navigate("/dashboard-intervenant");
            } else if (role === 4) {
                navigate("/dashboard-praticien"); // 👈 Redirection ajoutée pour le rôle Praticien
            } else {
                navigate("/dashboard-user");
            }
        } catch (err) {
            console.log("Login failed", err);

            // Récupération directe du message d'erreur de l'API Laravel
            const errorMsg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                t("loginErrorDefault", "Email ou mot de passe incorrect.");

            setApiError(errorMsg);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            }}
        >
            <Container maxWidth="xs">
                <Box sx={{ mb: 4, textAlign: "center" }}>
                    <Typography
                        variant="h4"
                        sx={{ color: "#fff", fontWeight: "800", letterSpacing: "-1px" }}
                    >
                        SOA <span style={{ fontWeight: "200" }}>Application</span>
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", mt: 1 }}>
                        {t("backOffice")}
                    </Typography>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        borderRadius: "24px",
                        boxShadow: "0px 10px 40px rgba(0,0,0,0.2)",
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", mb: 3, textAlign: "center" }}
                    >
                        {t("loginTitle")}
                    </Typography>

                    {/* Affichage direct et immédiat de l'erreur retournée par l'API */}
                    {apiError && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
                            {apiError}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("email")}
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                    InputProps={{ sx: { borderRadius: "12px" } }}
                                />
                            )}
                        />

                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("password")}
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    type={showPassword ? "text" : "password"}
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    InputProps={{
                                        sx: { borderRadius: "12px" },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClickShowPassword} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Box sx={{ mt: 1 }}>
                            <Controller
                                name="remember"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">{t("remember")}</Typography>
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{
                                mt: 3,
                                py: 1.5,
                                fontWeight: "bold",
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: "1rem",
                                backgroundColor: "#1e3c72",
                                "&:hover": { backgroundColor: "#2a5298" },
                            }}
                        >
                            {t("loginTitle")}
                        </Button>
                    </form>

                    {/* Footer avec lien YANACOM */}
                    <Typography variant="body2" align="center" sx={{ mt: 3, color: "#999" }}>
                        © 2026 Application SOA Dashboard{" "}
                        <Typography
                            component="a"
                            href="https://yanacom.fr/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: "#1e3c72",
                                textDecoration: "none",
                                fontWeight: "bold",
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            YANACOM
                        </Typography>
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;